"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionService = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const core_shared_1 = require("@ikegps/core-shared");
const page_state_1 = require("../state/page.state");
const store_1 = require("../store");
const index_1 = require("./index");
class CollectionService {
    constructor() {
        this.__priority = 100;
    }
    getFirstCaptureId(collection) {
        if (collection == null) {
            return null;
        }
        const captureIds = this.getCaptureIds(collection);
        return captureIds[0];
    }
    getCaptureIds(collection) {
        const captureIds = [];
        collection.forEachField(collectionField => {
            if (core_shared_1.CollectionModelFieldTrueSize.isTrueSizeField(collectionField) || core_shared_1.CollectionModelFieldImage.isImageField(collectionField)) {
                for (const capture of collectionField.getCaptures()) {
                    captureIds.push(capture.objectId);
                }
            }
            return true;
        });
        return captureIds;
    }
    remove(collectionSummaries) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (index_1.Services.Department.current == null) {
                throw new Error('Cannot delete collections without a current department');
            }
            const allUuids = collectionSummaries.map(c => c.uuid);
            const tagIds = this.getTagIds(collectionSummaries);
            try {
                const startTime = Date.now();
                // Batch deletions to avoid hitting server limit
                const uuidBatches = core_shared_1.IkeArray.chunk(allUuids, CollectionService.BATCH_SIZE);
                for (const uuids of uuidBatches) {
                    yield index_1.Services.Department.rest.Collection.delete({ uuids });
                }
                yield index_1.Services.Tag.refresh(tagIds);
                page_state_1.CurrentState.Tag.reset();
                page_state_1.CurrentState.setInspectedCollection(null);
                const label = `Tags: ${tagIds.length}, Collections: ${collectionSummaries.length}`;
                index_1.Services.Analytics.trackEvent(index_1.Services.Analytics.EventCategory.BigMap, 'COLLECTION_DELETE', label, Date.now() - startTime);
            }
            catch (e) {
                (0, index_1.handleGenericError)(e);
            }
        });
    }
    duplicate(collectionSummaries, tagName, toDepartmentId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const currentDepartment = index_1.Services.Department.current;
            if (currentDepartment == null) {
                throw new Error('Cannot duplicate collections without a current department');
            }
            let toDepartment;
            if (toDepartmentId != null && toDepartmentId !== currentDepartment.objectId) {
                toDepartment = yield index_1.Services.Department.departments.find(d => d.objectId === toDepartmentId);
                if (toDepartment == null) {
                    throw new Error('Cannot duplicate collections without a destination department');
                }
            }
            const allSelectors = collectionSummaries.map(summary => ({ uuid: summary.uuid }));
            const tagIds = this.getTagIds(collectionSummaries);
            try {
                const startTime = Date.now();
                // Batch modifications to avoid hitting server limit
                const selectorBatches = core_shared_1.IkeArray.chunk(allSelectors, CollectionService.BATCH_SIZE);
                for (const selectors of selectorBatches) {
                    yield index_1.Services.Department.rest.Collection.duplicate({ collections: selectors, tagName, toDepartmentId });
                }
                yield index_1.Services.Tag.refresh(tagIds);
                page_state_1.CurrentState.Tag.reset();
                page_state_1.CurrentState.setInspectedCollection(null);
                const label = toDepartment != null ? `"${currentDepartment.displayName}" -> "${toDepartment.displayName}"` : tagName;
                index_1.Services.Analytics.trackEvent(index_1.Services.Analytics.EventCategory.BigMap, 'COLLECTION_DUPLICATE', label, Date.now() - startTime);
            }
            catch (e) {
                (0, index_1.handleGenericError)(e);
            }
        });
    }
    assign(collectionSummaries, userId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            userId = userId == null ? undefined : userId;
            if (index_1.Services.Department.current == null) {
                throw new Error('Cannot assign collections without a current department');
            }
            const uuids = collectionSummaries.map(summary => summary.uuid);
            const allSelectors = uuids.map(uuid => ({ uuid }));
            const tagIds = this.getTagIds(collectionSummaries);
            try {
                const startTime = Date.now();
                // Batch modifications to avoid hitting server limit
                const selectorBatches = core_shared_1.IkeArray.chunk(allSelectors, CollectionService.BATCH_SIZE);
                for (const selectors of selectorBatches) {
                    yield index_1.Services.Department.rest.Collection.assign({ userId, collections: selectors });
                }
                yield page_state_1.CurrentState.updateInspectedCollection();
                yield this.refresh(uuids);
                const label = `Tags: ${tagIds.length}, Collections: ${collectionSummaries.length}`;
                if (userId != null) {
                    index_1.Services.Analytics.trackEvent(index_1.Services.Analytics.EventCategory.BigMap, 'COLLECTION_ASSIGN', label, Date.now() - startTime);
                }
                else {
                    index_1.Services.Analytics.trackEvent(index_1.Services.Analytics.EventCategory.BigMap, 'COLLECTION_UNASSIGN', label, Date.now() - startTime);
                }
            }
            catch (e) {
                (0, index_1.handleGenericError)(e);
            }
        });
    }
    move(collectionSummaries, tagName, targetDepartmentId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const currentDepartment = index_1.Services.Department.current;
            if (currentDepartment == null) {
                throw new Error('Cannot move collections without a current department');
            }
            let toDepartment;
            if (targetDepartmentId != null && targetDepartmentId !== currentDepartment.objectId) {
                toDepartment = yield index_1.Services.Department.departments.find(d => d.objectId === targetDepartmentId);
                if (toDepartment == null) {
                    throw new Error('Cannot move collections without a destination department');
                }
            }
            const allSelectors = collectionSummaries.map(summary => ({ uuid: summary.uuid }));
            const tagIds = this.getTagIds(collectionSummaries);
            try {
                const startTime = Date.now();
                // Batch modifications to avoid hitting server limit
                const selectorBatches = core_shared_1.IkeArray.chunk(allSelectors, CollectionService.BATCH_SIZE);
                for (const selectors of selectorBatches) {
                    if (targetDepartmentId == null || targetDepartmentId === currentDepartment.objectId) {
                        yield index_1.Services.Department.rest.Collection.moveToJob({ collections: selectors, tagName });
                    }
                    else {
                        yield index_1.Services.Department.rest.Collection.moveToDepartment({ collections: selectors, targetDepartmentId });
                    }
                }
                yield index_1.Services.Tag.refresh(tagIds);
                page_state_1.CurrentState.Tag.reset();
                page_state_1.CurrentState.setInspectedCollection(null);
                store_1.AppStore.TagSummary.clear();
                const label = toDepartment != null ? `"${currentDepartment.displayName}" -> "${toDepartment.displayName}"` : tagName;
                index_1.Services.Analytics.trackEvent(index_1.Services.Analytics.EventCategory.BigMap, 'COLLECTION_MOVE', label, Date.now() - startTime);
            }
            catch (e) {
                (0, index_1.handleGenericError)(e);
            }
        });
    }
    save(collection) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const rest = index_1.Services.Department.rest;
            const collectionJSON = yield core_shared_1.IkeModel.Collection.write(collection);
            const newCollectionJSON = yield rest.save(rest.Objects.Collection, collectionJSON);
            index_1.Services.Analytics.trackEvent(index_1.Services.Analytics.EventCategory.Mutate, 'Collection:save', collection.uuid, 1);
            yield this.refresh([newCollectionJSON.uuid]);
            yield store_1.AppStore.Collection.fetch(newCollectionJSON.revision);
            return newCollectionJSON;
        });
    }
    refresh(collectionUuids) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const collectionSummaries = yield Promise.all(collectionUuids.map(uuid => store_1.AppStore.CollectionSummary.getFetchableByUuid(uuid).refetch()));
            return Promise.all([
                store_1.AppStore.Tag.refresh(),
                ...this.getTagIds(collectionSummaries).map(tagId => store_1.AppStore.TagSummary.getFetchable(tagId).refetch())
            ]);
        });
    }
    getTagIds(collectionSummaries) {
        const tags = {};
        for (const collectionSummary of collectionSummaries) {
            for (const tag of collectionSummary.tags) {
                if (!tags[tag] && !(0, core_api_1.isSystemTag)(tag)) {
                    tags[tag] = true;
                }
            }
        }
        return Object.keys(tags);
    }
}
exports.CollectionService = CollectionService;
CollectionService.BATCH_SIZE = 50;
//# sourceMappingURL=collection.service.js.map